import { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "../_shared";
import { useApi, act, Pill, Panel, Btn, Field, inputCls, Table, Stat, fmtTs } from "./_kit";

export default function IntegrationHub() {
  const { data: prov, reload: r1 } = useApi("/integrations/providers");
  const { data: calls, reload: r2 } = useApi("/integrations/calls?limit=60");
  const { data: dlq, reload: r3 } = useApi("/integrations/dlq");
  const { data: recon, reload: r4 } = useApi("/integrations/reconciliation");
  const reload = () => { r1(); r2(); r3(); r4(); };
  const [sel, setSel] = useState(null);
  const p = (prov?.items || []).find((x) => x.name === sel);
  return (
    <div>
      <PageHeader subtitle="Integration Hub · adapters, idempotency, retry, DLQ, replay" title="Integrations"
        actions={<Btn variant="ghost" data-testid="ih-refresh" onClick={reload}>Refresh</Btn>} />
      <div className="p-6 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Providers" value={prov?.items?.length} />
          <Stat label="Live" value={(prov?.items || []).filter((x) => x.mode === "live").length} hint="others mock — awaiting credentials" />
          <Stat label="DLQ open" value={recon?.dlq_open} testid="ih-dlq-open" />
          <Stat label="Calls logged" value={(recon?.by_provider || []).reduce((a, b) => a + b.count, 0)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {(prov?.items || []).map((x) => (
            <button key={x.name} data-testid={`ih-provider-${x.name}`} onClick={() => setSel(x.name)}
              className={`text-left bg-white border rounded-sm p-4 transition-colors ${sel === x.name ? "border-[#5A1A22]" : "border-black/10 hover:border-black/30"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-semibold">{x.label}</div>
                <Pill value={x.mode} />
              </div>
              <div className="text-[11px] text-neutral-500 mt-1">{x.category} · {x.operations.length} ops</div>
              {x.mode === "mock" && <div className="mt-2 text-[10px] uppercase tracking-[0.15em] text-amber-800 bg-amber-50 inline-block px-1.5 py-0.5 rounded-sm">Mock — awaiting credentials</div>}
            </button>
          ))}
        </div>
        {p && <ProviderEditor p={p} onDone={reload} />}
        <div className="grid grid-cols-12 gap-4">
          <Panel className="col-span-12 xl:col-span-7" title="Recent calls" eyebrow="Every outbound call logged" testid="ih-calls">
            <Table rows={calls?.items} cols={[
              { key: "created_at", label: "When", render: (r) => fmtTs(r.created_at) }, { key: "provider", label: "Provider" },
              { key: "operation", label: "Op" }, { key: "mode", label: "Mode", render: (r) => <Pill value={r.mode} /> },
              { key: "status", label: "Status", render: (r) => <Pill value={r.status} /> }, { key: "attempts", label: "Tries" },
              { key: "duration_ms", label: "ms" }, { key: "idempotency_key", label: "Idem key", render: (r) => <code className="text-[10px]">{r.idempotency_key?.slice(0, 28)}</code> },
            ]} />
          </Panel>
          <Panel className="col-span-12 xl:col-span-5" title="Dead-letter queue" eyebrow="Replay after fixing the cause" testid="ih-dlq">
            <Table rows={dlq?.items} cols={[
              { key: "created_at", label: "When", render: (r) => fmtTs(r.created_at) }, { key: "provider", label: "Provider" },
              { key: "operation", label: "Op" }, { key: "error", label: "Error", render: (r) => <span className="text-rose-700">{r.error?.slice(0, 60)}</span> },
              { key: "status", label: "", render: (r) => r.status === "dead" ? <Btn variant="ghost" data-testid={`ih-replay-${r.id}`} onClick={async () => { await act(api.post(`/integrations/dlq/${r.id}/replay`), "Replayed"); reload(); }}>Replay</Btn> : <Pill value={r.status} /> },
            ]} />
          </Panel>
        </div>
      </div>
    </div>
  );
}

function ProviderEditor({ p, onDone }) {
  const [creds, setCreds] = useState({});
  const [fail, setFail] = useState(p.mock_failure_rate ?? 0);
  const [op, setOp] = useState(p.operations[0]);
  const save = (extra) => act(api.put(`/integrations/providers/${p.name}`, { credentials: creds, mock_failure_rate: Number(fail), ...extra }), "Provider updated").then(onDone);
  return (
    <Panel title={p.label} eyebrow={`Adapter contract · ${p.operations.join(", ")}`} testid="ih-provider-editor"
      right={<><Btn variant="ghost" data-testid="ih-mode-mock" onClick={() => save({ mode: "mock" })}>Mock</Btn><Btn variant="gold" data-testid="ih-mode-live" onClick={() => save({ mode: "live" })}>Go live</Btn><Btn variant="danger" data-testid="ih-mode-disabled" onClick={() => save({ mode: "disabled" })}>Disable</Btn></>}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {p.credential_fields.map((f) => (
          <Field key={f} label={f} hint={p.credentials?.[f] ? `stored ${p.credentials[f]}` : "not set"}>
            <input data-testid={`ih-cred-${f}`} className={inputCls} type="password" placeholder="paste credential" onChange={(e) => setCreds({ ...creds, [f]: e.target.value })} />
          </Field>
        ))}
        <Field label="Mock failure rate (0–1)" hint="simulate provider outages to test retry + DLQ">
          <input data-testid="ih-fail-rate" className={inputCls} type="number" step="0.1" min="0" max="1" value={fail} onChange={(e) => setFail(e.target.value)} />
        </Field>
      </div>
      <div className="flex flex-wrap items-end gap-2 mt-4">
        <Btn data-testid="ih-save-creds" onClick={() => save({})}>Save</Btn>
        <select data-testid="ih-test-op" className={inputCls + " max-w-[220px]"} value={op} onChange={(e) => setOp(e.target.value)}>{p.operations.map((o) => <option key={o}>{o}</option>)}</select>
        <Btn variant="ghost" data-testid="ih-test-call" onClick={async () => { await act(api.post("/integrations/call", { provider: p.name, operation: op, payload: { to: "9999999999", test: true } }), "Test call executed"); onDone(); }}>Fire test call</Btn>
      </div>
    </Panel>
  );
}
